import React, { useEffect, useRef, useState } from "react";
import styles from "./Accordion.module.css";
import CardWrapper from "../CardWrapper";
import { Button } from "../Modal";
import { IPatient } from "../../hooks/patients/index.type";
import { IChat } from "../../hooks/chats/index.type";
import ChatSkeleton from "../../Skeletons/ChatSkeleton";
import { decryptMessage, EncryptedMessage } from "../../utils/decryptMessage";
import useResendQuestion from "../../hooks/billingList/useResendQuestion";
import useFetchChat from "../../hooks/chats/useFetchChat";
import toast from "react-hot-toast";

interface ChatCardProps {
  title?: string;
  isDisabled?: boolean;
  patient: IPatient;
  chat: IChat[];
  loading: boolean;
  decryptPrivateKeyData: CryptoKey | null;
  isAlertMessagesPresent: "yes" | "no";
  refetchChats?: () => Promise<{
    success: boolean;
    data?: any;
    error?: string;
  }>;
}

const ChatCard: React.FC<ChatCardProps> = ({
  title = "Chat Transmissions",
  patient,
  chat,
  loading,
  decryptPrivateKeyData,
  isAlertMessagesPresent,
  refetchChats,
  isDisabled = false,
}) => {
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const [decryptedChats, setDecryptedChats] = useState<
    (IChat & { decryptedMessage?: string })[]
  >([]);
  const [decryptError, setDecryptError] = useState<string | null>(null);
  const [isResending, setIsResending] = useState(false);

  const { resendQuestion, resendingQuestion } = useResendQuestion();
  const { getChatData: fetchChat } = useFetchChat({ patientId: patient?._id });

  const sendViaChat = async (patientId: string) => {
    try {
      const chatResponse = await fetchChat();

      if (!chatResponse?.success) {
        toast.error(
          `Failed to send chat: ${chatResponse?.error || "Unknown error"}`
        );
        return false;
      }

      await refetchChats?.();
      return true;
    } catch {
      toast.error("Failed to send chat notification. Please try again.");
      return false;
    }
  };

  const handleResendClick = async () => {
    if (!patient?._id) {
      toast.error("Patient ID not found");
      return;
    }

    setIsResending(true);
    try {
      const response = await resendQuestion({ patientId: patient._id });

      if (response?.status === "fail") {
        return;
      }

      // Call refetchChats to update the chat data
      if (refetchChats) {
        await refetchChats();
      }
    } catch (error) {
      console.error("Error in handleResendClick:", error);
      toast.error("Failed to resend question. Please try again.");
    } finally {
      setIsResending(false);
    }
  };

  const visibleChats = decryptedChats.filter(
    (chatItem) => chatItem.visibility === "yes"
  );

  useEffect(() => {
    if (chatContainerRef.current && visibleChats.length > 0) {
      chatContainerRef.current.scrollTop =
        chatContainerRef.current.scrollHeight;
    }
  }, [visibleChats]);

  useEffect(() => {
    if (!chat || chat.length === 0) {
      setDecryptedChats([]);
      return;
    }

    const decryptAllMessages = async () => {
      try {
        const processed = await Promise.all(
          chat
            .filter((msg) => msg.visibility === "yes")
            .map(async (msg) => {
              if (msg.encrypted === "yes") {
                if (!decryptPrivateKeyData) {
                  return {
                    ...msg,
                    decryptedMessage: "[Decryption key not available]",
                  };
                }

                if (
                  typeof msg.message === "string" &&
                  msg.encryptedAesKey &&
                  msg.iv
                ) {
                  const encryptedMsg: EncryptedMessage = {
                    ephemeralPublicKey: msg.encryptedAesKey,
                    iv: msg.iv,
                    encryptedMessage: msg.message,
                  };

                  try {
                    const privateKeyRaw = await crypto.subtle.exportKey(
                      "pkcs8",
                      decryptPrivateKeyData
                    );
                    const plainText = await decryptMessage(
                      encryptedMsg,
                      privateKeyRaw
                    );
                    return { ...msg, decryptedMessage: plainText };
                  } catch {
                    return { ...msg, decryptedMessage: "[Failed to decrypt]" };
                  }
                }

                return {
                  ...msg,
                  decryptedMessage: "[Invalid encrypted message format]",
                };
              }

              return { ...msg, decryptedMessage: String(msg.message) };
            })
        );

        setDecryptedChats(processed);
        setDecryptError(null);
      } catch {
        setDecryptError(
          "Message processing failed. Check console for details."
        );
        setDecryptedChats([]);
      }
    };

    decryptAllMessages();
  }, [chat, decryptPrivateKeyData]);

  const EmptyChats = () => (
    <div className={styles.emptyNotes}>
      <svg>
        <use xlinkHref="/assets/sprite.svg#icon-chat-empty" />
      </svg>
      <p>No chat history available</p>
      <span>Chat messages will appear here</span>
    </div>
  );

  function formatParagraph(text: string) {
    return text.replace(/\\n/g, "\n").replace(/\\t/g, "\t");
  }

  return (
    <CardWrapper>
      <div className={styles.noteSubHeader}>
        <div className={styles.title}>{title}</div>
        <div className={styles.stopMessageContainer}>
          {isAlertMessagesPresent === "yes" && (
            <span className={styles.stopMessageText}>
              STOP message received
            </span>
          )}
          <Button
            label={
              <>
                <svg className={styles.reloadIcon}>
                  <use xlinkHref="/assets/sprite.svg#icon-reload" />
                </svg>
                {resendingQuestion ? "Sending..." : "Resend"}
              </>
            }
            onClick={handleResendClick}
            disabled={
              resendingQuestion ||
              isDisabled ||
              patient?.rtmStatus?.toLowerCase() === "inactive"
            }
            customClass={`${styles.resendButton} ${
              isDisabled ? styles.disabledButton : ""
            }`}
          />
        </div>
      </div>

      <div className={styles.note_card_container} style={{ height: "32.6rem" }}>
        {isResending || loading ? (
          <ChatSkeleton count={3} />
        ) : isDisabled ? (
          <div className={styles.emptyNotes}>
            <svg>
              <use xlinkHref="/assets/sprite.svg#icon-chat-empty" />
            </svg>
            <p>Chat is not available.</p>
            <span>
              Chat functionality is disabled for{" "}
              {patient.rtmStatus?.toLowerCase()} patients.
            </span>
          </div>
        ) : visibleChats.length > 0 ? (
          <div ref={chatContainerRef} className={styles.chatContainer}>
            {visibleChats.map((item) => {
              const isSender = item.from === item.patientId;
              const timestamp = Number(item.createdTimestamp);
              const createdAt = new Date(timestamp);
              const isValidDate = !isNaN(createdAt.getTime());
              const isMessageFailed = item.isMessageSent === "no";

              return (
                <div
                  key={item._id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: isSender ? "flex-end" : "flex-start",
                    width: "100%",
                  }}
                >
                  <CardWrapper
                    customClass={`${styles.chatBox} ${
                      isSender ? styles.receiveChat : styles.sendChat
                    }`}
                  >
                    <pre
                      className={styles.noteValue}
                      style={{ whiteSpace: "pre-wrap" }}
                    >
                      {item.decryptedMessage || "Message not available"}
                    </pre>
                    <p className={styles.chatTimestamp}>
                      {isValidDate
                        ? `${createdAt.toLocaleDateString([], {
                            year: "numeric",
                            month: "short",
                            day: "2-digit",
                          })} | ${createdAt.toLocaleTimeString([], {
                            hour: "2-digit",
                            minute: "2-digit",
                            hour12: true,
                          })}`
                        : "Date not available"}
                    </p>
                  </CardWrapper>
                  {isMessageFailed && (
                    <div
                      className={styles.warningIconContainer}
                      data-tooltip="Undelivered"
                    >
                      <svg className={styles.warningIcon}>
                        <use xlinkHref="/assets/sprite.svg#icon-undelivered-red" />
                      </svg>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <EmptyChats />
        )}

        {decryptError && (
          <div
            className={styles.error}
            style={{ color: "red", marginTop: "1rem" }}
          >
            {decryptError}
          </div>
        )}
      </div>
    </CardWrapper>
  );
};

export default ChatCard;
